import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import {
  type CreateTriggerV2Request,
  type TriggerV2Record,
  type UpdateTriggerV2Request,
} from "../TriggerV2Record";

import { triggersV2QueryKeys } from "./triggersV2QueryKeys";

export function useCreateTriggerV2(orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateTriggerV2Request) =>
      triggerV2Service.createTrigger(request, resolvedOrgId),
    onSuccess: (record: TriggerV2Record) => {
      queryClient.setQueryData(
        triggersV2QueryKeys.byName(resolvedOrgId, record.name),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: triggersV2QueryKeys.list(resolvedOrgId),
      });
    },
  });
}

export function useUpdateTriggerV2(orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      updates,
    }: {
      name: string;
      updates: UpdateTriggerV2Request;
    }) => triggerV2Service.updateTrigger(name, updates, resolvedOrgId),
    onSuccess: (record: TriggerV2Record) => {
      queryClient.setQueryData(
        triggersV2QueryKeys.byName(resolvedOrgId, record.name),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: triggersV2QueryKeys.list(resolvedOrgId),
      });
    },
  });
}

export function useSetTriggerV2Enabled(orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      triggerV2Service.setEnabled(name, enabled, resolvedOrgId),
    onSuccess: (record: TriggerV2Record) => {
      queryClient.setQueryData(
        triggersV2QueryKeys.byName(resolvedOrgId, record.name),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: triggersV2QueryKeys.list(resolvedOrgId),
      });
    },
  });
}

export function useDeleteTriggerV2(orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      triggerV2Service.deleteTrigger(name, resolvedOrgId),
    onSuccess: (_, { name }) => {
      queryClient.removeQueries({
        queryKey: triggersV2QueryKeys.byName(resolvedOrgId, name),
      });
      void queryClient.invalidateQueries({
        queryKey: triggersV2QueryKeys.list(resolvedOrgId),
      });
    },
  });
}
